// pending....
import React, { FormEvent, useEffect, useState } from "react";
import {
  faFloppyDisk,
  faPlus,
  faSpinner,
  faSubtract,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SimpleInput from "../SimpleInput";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import ImageUpload from "../GlobalComponents/ImageUplaod";
import InputsModal from "../GlobalComponents/InputsModal";
import ConfirmationModal from "../GlobalComponents/ConfirmationModal";
import TableWrapper from "../GlobalComponents/TableWrapper";
import editIcon from "src/assets/images/edit-icon.png";
import deleteIcon from "src/assets/images/delete-icon.png";
import {
  convertToKebabCase,
  generateRandomKey,
  truncateString,
} from "src/helpers/helperFunctions";
import { toast } from "react-toastify";
import SearchFilterInput from "../GlobalComponents/SearchFilterInput";
import {
  IDiagnosticProductObj,
  IqueryParams,
} from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import httpRequest from "src/helpers/httpRequest";

interface ISinglePackageGet {
  _id: string;
  name: string;
  description: string;
  prescriptionRequired: boolean;
  regularPrice: string;
  salePrice: string | null;
  tests: { name: string; id: string }[];
  faq: { question: string; answer: string; _id: string }[];
  instructions: IInstObj[];
  image: string;
}

interface IProductResponse {
  documents: IDiagnosticProductObj[];
  paginated: IPaginated;
}

interface IInstObj {
  instructionDetail: string;
  _id: string;
}

interface IDiagnosticsPackagesCreate {
  name: string;
  description: string;
  prescriptionRequired: boolean;
  regularPrice: string;
  salePrice: string | null;
  testIds: string[];
  instructions: IInstObj[];
  faq: { question: string; answer: string; _id: string }[];
  image: string | File | null;
}

interface IProps {
  isEdit: string | null;
  onSuccess: () => void;
}

type tabKeys =
  | "General"
  | "Data"
  | "Add Products"
  | "Instructions"
  | "Image"
  | "FAQ's";

const UpsertPackage: React.FC<IProps> = ({
  isEdit = null,
  onSuccess = () => {},
}) => {
  const tabsArray: tabKeys[] = [
    "General",
    "Data",
    "Add Products",
    "Instructions",
    "FAQ's",
    "Image",
  ];
  const [activeTab, setActiveTab] = useState<tabKeys>("General");
  const handleTabClick = (tabName: tabKeys) => {
    setActiveTab(tabName);
  };

  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [payload, setPayload] = useState<IDiagnosticsPackagesCreate>({
    name: "",
    description: "",
    prescriptionRequired: false,
    regularPrice: "",
    salePrice: "",
    testIds: [],
    faq: [],
    instructions: [],
    image: null,
  });

  const [inst, setInst] = useState<string>(""); // State to handle the new instruction input value
  const [instEdit, setInstEdit] = useState<boolean>(false); // State to control the visibility of the edit modal
  const [editInst, setEditInst] = useState<string>(""); // State to hold the instruction being edited
  const [isDelete, setIsDelete] = useState<boolean>(false); // State to control the visibility of the delete confirmation modal
  const [instId, setInstId] = useState<string>(""); // State to hold the ID of the instruction being deleted or edited

  // test states & functions
  const [testsLoader, setTestsLoader] = useState<boolean>(false);
  const [selectedTest, setSelectedTest] = useState<
    { name: string; id: string }[]
  >([]);
  const [allTest, setAllTest] = useState<IDiagnosticProductObj[]>([]);
  const handleAddOrRemoveTest = (id: string, name: string = "") => {
    setSelectedTest((prev) => {
      const exists = prev.some((marker) => marker.id === id);
      if (exists) {
        return prev.filter((marker) => marker.id !== id);
      } else {
        return [...prev, { name, id }];
      }
    });
    setPayload((prev) => {
      const isIdInArray = prev?.testIds?.includes(id);
      if (isIdInArray) {
        // add in array
        return {
          ...prev,
          testIds: prev?.testIds?.filter((bi) => bi !== id),
        };
      } else {
        // Otherwise, add the id to the array
        return {
          ...prev,
          testIds: [...prev?.testIds, id],
        };
      }
    });
  };
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // state for handle pagination like a current page, page limit & total pages
  const [testQueries, setTestQueries] = useState<IqueryParams>({
    page: 1,
    limit: 10,
    search: "",
  }); // State for query parameters in API requests
  const changeQuery = (txt: string) => {
    setTestQueries((prev) => ({
      ...prev,
      page: 1,
      search: txt,
    }));
  };
  // Function to handle page change in pagination
  const handlePageChange = (e: number) => {
    setTestQueries((prev) => ({
      ...prev,
      page: e,
    }));
  };
  const fetchAllProducts = async () => {
    setTestsLoader(true);
    const { res, err } = await httpRequest<IProductResponse>({
      path: `/product/all?${qs.stringify(testQueries)}`,
    });
    if (res) {
      setAllTest(res?.documents);
      setPaginated(res?.paginated);
    } else {
      toast(err, { type: "error" });
    }
    setTestsLoader(false);
  };
  useEffect(() => {
    fetchAllProducts();
  }, [testQueries]);
  // test states & functions

  const addInst = (event: FormEvent) => {
    event.preventDefault();
    if (payload?.instructions.length >= 5) return;
    let obj: IInstObj = {
      instructionDetail: inst,
      _id: generateRandomKey(),
    };
    // Update the state with the new instruction
    setPayload((prev) => ({
      ...prev,
      instructions: [...prev.instructions, obj],
    }));
    setInst("");
  };

  // Set states for editing an instruction
  const handleEditInst = (obj: IInstObj): void => {
    setInstEdit(true);
    setInstId(obj?._id);
    setEditInst(obj?.instructionDetail);
  };

  // Set states for deleting an instruction
  const handleDeleteInst = (id: string): void => {
    setIsDelete(true);
    setInstId(id);
  };

  // Close modals and reset relevant states
  const modalClose = (): void => {
    setInstEdit(false);
    setIsDelete(false);
    setInstId("");
  };

  // Delete an instruction from the state
  const deleteInst = (): void => {
    console.log(instId);
    setPayload((prev) => ({
      ...prev,
      instructions: prev?.instructions?.filter((v) => v?._id !== instId),
    }));
    modalClose();
  };

  // Update an instruction in the state
  const updateInst = () => {
    let Arr = payload?.instructions?.map((v) => {
      if (v?._id === instId) {
        return {
          ...v,
          instructionDetail: editInst,
        };
      }
      return v;
    });
    setPayload((prev) => ({
      ...prev,
      instructions: Arr,
    }));
    modalClose();
  };

  const handleChange = (key: keyof IDiagnosticsPackagesCreate, val: string) => {
    setPayload((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  // faqs
  const [faqState, setFaqState] = useState<{
    question: string;
    answer: string;
    faqEdit: boolean;
    deleteFaqs: boolean;
    faqId: string;
    editQuestion: string;
    editAnswer: string;
  }>({
    question: "",
    answer: "",
    faqEdit: false,
    deleteFaqs: false,
    faqId: "",
    editQuestion: "",
    editAnswer: "",
  });
  const handleEditfaqs = (obj: {
    question: string;
    answer: string;
    _id: string;
  }) => {
    setFaqState((prev) => ({
      ...prev,
      faqEdit: true,
      faqId: obj?._id,
      editQuestion: obj?.question,
      editAnswer: obj?.answer,
    }));
  };
  const updateFaqs = () => {
    let Arr = payload?.faq?.map((v) => {
      if (v?._id === faqState?.faqId) {
        return {
          ...v,
          question: faqState?.editQuestion,
          answer: faqState?.editAnswer,
        };
      }
      return v;
    });
    setPayload((prev) => ({
      ...prev,
      faq: Arr,
    }));
    setFaqState((prev) => ({
      ...prev,
      faqEdit: false,
      deleteFaqs: false,
      faqId: "",
      editQuestion: "",
      editAnswer: "",
    }));
  };
  const handleDeleteFaqs = (id: string) => {
    setFaqState((prev) => ({
      ...prev,
      deleteFaqs: true,
      faqId: id,
    }));
  };

  const handleSubmitFaqs = (event: FormEvent) => {
    event.preventDefault();
    // Create new FAQ object with a unique ID
    let obj = {
      question: faqState?.question,
      answer: faqState?.answer,
      _id: generateRandomKey(),
    };
    // Update the state with the new FAQ
    setPayload((prev) => ({
      ...prev,
      faq: [...prev.faq, obj],
    }));
    // Reset input fields
    setFaqState((prev) => ({
      ...prev,
      question: "",
      answer: "",
    }));
  };

  const handleSubmit = async () => {
    const {
      name,
      description,
      prescriptionRequired,
      regularPrice,
      salePrice,
      testIds,
      instructions,
      image,
      faq,
    } = payload;
    // Validate required fields
    if (!name || !description) {
      toast("Please fill out all general details before proceeding.", {
        type: "error",
      });
      setActiveTab("General");
      return;
    } else if (!regularPrice) {
      toast("Please specify the regular price of the package.", {
        type: "error",
      });
      setActiveTab("Data");
      return;
    } else if (Number(salePrice || 0) >= Number(regularPrice)) {
      toast(
        "Sale price cannot be greater than or equal to the regular price.",
        { type: "error" }
      );
      setActiveTab("Data");
    } else if (testIds.length < 2) {
      toast("Please select at least two tests.", { type: "error" });
      setActiveTab("Add Products");
      return;
    } else if (instructions?.length < 2) {
      toast("At least 2 test instruction is required.", { type: "error" });
      setActiveTab("Instructions");
      return;
    } else if (!image) {
      toast("Package image is required.", { type: "error" });
      setActiveTab("Image");
    } else if (faq?.length < 2) {
      toast("At least 2 FAQ's is required.", { type: "error" });
      setActiveTab("Instructions");
      return;
    } else {
      setIsLoader(true);
      const formData = new FormData();
      formData.append("name", name);
      formData.append("description", description);
      formData.append("prescriptionRequired", String(prescriptionRequired));
      formData.append("regularPrice", regularPrice);
      formData.append("salePrice", salePrice || "");
      formData.append("testIds", JSON.stringify(testIds));
      formData.append("faq", JSON.stringify(faq));
      formData.append("instructions", JSON.stringify(instructions));
      formData.append("image", image);
      const path = isEdit
        ? `/diagnostic-package/edit/${isEdit}`
        : "/diagnostic-package/add";
      const { res, err } = await httpRequest<any>({
        method: isEdit ? "put" : "post",
        path,
        params: formData,
        header: { "Content-Type": "multipart/form-data" },
      });
      if (res) {
        toast(
          `Package ${isEdit ? "updated" : "added"} successfully.`,
          { type: "success" }
        );
        onSuccess();
        setPayload({
          name: "",
          description: "",
          prescriptionRequired: false,
          regularPrice: "",
          salePrice: "",
          testIds: [],
          faq: [],
          instructions: [],
          image: null,
        });
      } else {
        toast(err, { type: "error" });
      }
      setIsLoader(false);
    }
  };

  const getPackageById = async () => {
    const { res, err } = await httpRequest<ISinglePackageGet>({
      path: `/diagnostic-package/single/${isEdit}`,
    });
    if (res) {
      const data = res;
      setSelectedTest(data?.tests);
      const newObj: IDiagnosticsPackagesCreate = {
        ...data,
        testIds: data?.tests?.map((v) => v.id),
      };
      setPayload(newObj);
    } else {
      toast(err, { type: "error" });
    }
  };

  useEffect(() => {
    if (isEdit) {
      getPackageById();
    }
  }, [isEdit]);

  return (
    <div className="relative">
      <button
        className="absolute save-pharmacy-and-product border-[#333] border rounded"
        disabled={isLoader}
        onClick={handleSubmit}
        id="save-package-btn"
      >
        <FontAwesomeIcon
          spin={isLoader}
          className="text-sm"
          icon={isLoader ? faSpinner : faFloppyDisk}
        />
      </button>
      <div className="products-tabs-btn">
        {tabsArray.map((item: tabKeys) => (
          <button
            key={item}
            className={`font-size-15px font-Poppins-Medium capitalize ${
              activeTab === item ? "active" : ""
            }`}
            onClick={() => handleTabClick(item)}
            id={`tab-${convertToKebabCase(item)}`}
          >
            {item}
          </button>
        ))}
      </div>
      <hr
        className="products-tabs-hr"
        style={{ border: "1px solid #D4D4D4" }}
      />
      <div className="parent-box-scroll-bar-product">
        {activeTab === "General" ? (
          <div className="products-tabs-content-main">
            <div className="products-tabs-content">
              <SimpleInput
                value={payload?.name}
                onChange={(val) => handleChange("name", val)}
                isLabel={true}
                labelText="Package Name"
                maxLength={50}
                id="package-name-input"
              />
              <SimpleInput
                type="textArea"
                rows={5}
                value={payload?.description}
                onChange={(val) => handleChange("description", val)}
                isLabel={true}
                labelText="Description"
                isLimit={500}
                maxLength={500}
                id="package-description-input"
              />
              <div className="flex items-center gap-2">
                <input
                  checked={payload?.prescriptionRequired || false}
                  type="checkbox"
                  id="diagnostic-prescriped"
                  onChange={() =>
                    setPayload((prev) => ({
                      ...prev,
                      prescriptionRequired: !prev?.prescriptionRequired,
                    }))
                  }
                />
                <label
                  htmlFor="diagnostic-prescriped"
                  className="font-size-18px font-Poppins-Regular"
                >
                  Prescription Required
                </label>
              </div>
            </div>
            <div className="flex justify-end my-6">
              <PrimaryBtn
                ButtonText="Next"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                }}
                onPress={() => handleTabClick("Data")}
                id="next-btn"
              />
            </div>
          </div>
        ) : activeTab === "Data" ? (
          <div className="products-tabs-content-main">
            <div className="products-tabs-content">
              <SimpleInput
                type="number"
                value={payload?.regularPrice}
                onChange={(val) => {
                  handleChange("regularPrice", val ? parseFloat(val || "0").toString() : "")
                }}
                maxLength={7}
                isLabel={true}
                labelText="Regular Price"
                onKeyDown={(e: any) => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                id="regular-price-input"
              />
              <SimpleInput
                type="number"
                value={payload?.salePrice || ""}
                onChange={(val) => {
                  handleChange("salePrice", val ? parseFloat(val || "0").toString() : "")
                }}
                isLabel={true}
                maxLength={7}
                labelText="Sale Price"
                onKeyDown={(e: any) => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                id="sale-price-input"
              />
            </div>
            <div className="flex justify-end my-6">
              <PrimaryBtn
                ButtonText="Next"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                }}
                onPress={() => handleTabClick("Add Products")}
                id="next-btn"
              />
            </div>
          </div>
        ) : activeTab === "Add Products" ? (
          <div>
            <div className="product-tabs-add-biomarker">
              <h3 className="font-size-20px text-[#4F6367] font-Poppins-Medium">
                Products
              </h3>
              <div className="add-biomarker-box-main">
                {payload?.testIds?.length > 0 ? (
                  selectedTest.map((item, index) => (
                    <div key={item.id} className="add-biomarker-box relative">
                      <div
                        onClick={() => handleAddOrRemoveTest(item.id)}
                        className="add-biomarker-box-svg"
                        id={`remove-test-btn-${index + 1}`}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </div>
                      <p className="font-size-16px font-Poppins-Regular">
                        {item.name}
                      </p>
                    </div>
                  ))
                ) : (
                  <p>Please add tests from the list below.</p>
                )}
              </div>
            </div>
            <hr
              className="products-tabs-hr"
              style={{ border: "1px solid #D4D4D4" }}
            />
            <SearchFilterInput onChangeText={changeQuery} />
            <TableWrapper
              isLoading={testsLoader}
              thead={["S.No", "Products", "Description", "Action"]}
              page={paginated?.currentPage || 1}
              total={paginated?.totalPages || 0}
              columnWidth="10% 30% 50% 10%"
              onChange={handlePageChange}
            >
              {allTest.map((item: IDiagnosticProductObj, index: number) => (
                <ul
                  key={index}
                  className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                  style={{ gridTemplateColumns: "10% 30% 50% 10%" }}
                >
                  <li>
                    {(paginated?.currentPage - 1) * testQueries.limit +
                      (index + 1)}
                  </li>
                  <li>{item?.productName || ""}</li>
                  <li>{truncateString(item?.description, 200) || ""}</li>
                  <li className="action-last-colum">
                    <FontAwesomeIcon
                      icon={
                        payload?.testIds?.includes(item?._id)
                          ? faSubtract
                          : faPlus
                      }
                      onClick={() =>
                        handleAddOrRemoveTest(item?._id, item?.productName)
                      }
                      id={`add-or-remove-test-btn-${index + 1}`}
                    />
                  </li>
                </ul>
              ))}
            </TableWrapper>
            <div className="flex justify-end my-6">
              <PrimaryBtn
                ButtonText="Next"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                }}
                onPress={() => handleTabClick("Instructions")}
                id="next-btn"
              />
            </div>
          </div>
        ) : activeTab === "Instructions" ? (
          <div>
            <InputsModal
              btnText="Update"
              show={instEdit}
              onClose={modalClose}
              onSubmit={updateInst}
              arr={[
                {
                  value: editInst,
                  setValue: (val) => setEditInst(val),
                  title: "Instruction",
                  type: "textArea",
                  isRequired: true,
                },
              ]}
            />
            {/* Modal for confirming deletion */}
            <ConfirmationModal
              show={isDelete}
              onClose={modalClose}
              title="Are you sure you want to delete?"
              leftBtnText="No"
              rightBtnText="Yes"
              onConfirm={deleteInst}
              loader={false}
            />
            {/* Table displaying test instructions */}
            <TableWrapper
              thead={["S.No", "Instruction", "Action"]}
              total={payload?.instructions?.length < 1 ? 0 : 1}
              columnWidth={"10% 80% 10%"}
            >
              {payload?.instructions?.map((v: IInstObj, i: number) => (
                <ul
                  key={i}
                  className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                  style={{ gridTemplateColumns: "10% 80% 10%" }}
                >
                  <li>{i + 1}</li>
                  <li className="break-all">{v?.instructionDetail || ""}</li>
                  <li className="action-last-colum">
                    <img
                      onClick={() => handleEditInst(v)}
                      src={editIcon}
                      alt=""
                      id={`edit-instruction-btn-${i + 1}`}
                    />
                    <img
                      onClick={() => handleDeleteInst(v?._id)}
                      src={deleteIcon}
                      alt=""
                      id={`delete-instruction-btn-${i + 1}`}
                    />
                  </li>
                </ul>
              ))}
            </TableWrapper>
            {/* Form for adding new test instructions */}
            <div className="products-tabs-content-main">
              <form onSubmit={addInst}>
                <div className="products-tabs-content">
                  <SimpleInput
                    value={inst}
                    onChange={(val) => setInst(val)}
                    isLabel={true}
                    labelText={"Test Instruction"}
                    type={"textArea"}
                    rows={5}
                    required={true}
                    maxLength={300}
                    isLimit={300}
                    disabled={payload?.instructions.length >= 5}
                    id="test-instruction-input"
                  />
                </div>
                <div className="flex justify-between my-6">
                  <PrimaryBtn
                    ButtonText="Add New Package Instruction"
                    btnExtraStyle={{
                      backgroundColor: "#00443f",
                      borderRadius: "1.5rem",
                    }}
                    type="submit"
                    disabled={payload?.instructions.length >= 5}
                    id="add-new-package-instruction-btn"
                  />
                  <PrimaryBtn
                    ButtonText="Next"
                    btnExtraStyle={{
                      backgroundColor: "#00443f",
                      borderRadius: "1.5rem",
                    }}
                    onPress={() => handleTabClick("FAQ's")}
                    type="button"
                    id="next-btn"
                  />
                </div>
              </form>
            </div>
          </div>
        ) : activeTab === "FAQ's" ? (
          <div>
            <InputsModal
              btnText="Update"
              show={faqState?.faqEdit}
              onClose={() => {
                setFaqState((prev) => ({
                  ...prev,
                  faqEdit: false,
                  deleteFaqs: false,
                  editQuestion: "",
                  editAnswer: "",
                }));
              }}
              onSubmit={updateFaqs}
              arr={[
                {
                  value: faqState?.editQuestion,
                  setValue: (val) =>
                    setFaqState((prev) => ({ ...prev, editQuestion: val })),
                  title: "Question",
                  type: "textArea",
                  isRequired: true,
                },
                {
                  value: faqState?.editAnswer,
                  setValue: (val) =>
                    setFaqState((prev) => ({ ...prev, editAnswer: val })),
                  title: "Answer",
                  type: "textArea",
                  isRequired: true,
                },
              ]}
            />
            <ConfirmationModal
              show={faqState?.deleteFaqs}
              onClose={() => {
                setFaqState((prev) => ({
                  ...prev,
                  faqEdit: false,
                  deleteFaqs: false,
                  editQuestion: "",
                  editAnswer: "",
                }));
              }}
              title="Are you sure you want to delete?"
              leftBtnText="No"
              rightBtnText="Yes"
              onConfirm={() => {
                setPayload((prev) => ({
                  ...prev,
                  faq: prev?.faq?.filter((v) => v?._id !== faqState?.faqId),
                }));
                setFaqState((prev) => ({
                  ...prev,
                  faqEdit: false,
                  deleteFaqs: false,
                  editQuestion: "",
                  editAnswer: "",
                }));
              }}
              loader={false}
            />
            <TableWrapper
              thead={["S.No", "Question", "Answer", "Action"]}
              total={payload?.faq?.length < 1 ? 0 : 1}
              columnWidth={"10% 40% 40% 10%"}
            >
              {payload?.faq?.map((v, i: number) => (
                <ul
                  key={i}
                  className="font-size-16px tbody-light-table font-Poppins-Regular grid"
                  style={{ gridTemplateColumns: "10% 40% 40% 10%" }}
                >
                  <li>{i + 1}</li>
                  <li className="break-all">{v?.question || ""}</li>
                  <li className="break-all">{v?.answer || ""}</li>
                  <li className="action-last-colum">
                    <img
                      onClick={() => handleEditfaqs(v)}
                      src={editIcon}
                      alt=""
                      id={`edit-faq-btn-${i + 1}`}
                    />
                    <img
                      onClick={() => handleDeleteFaqs(v?._id)}
                      src={deleteIcon}
                      alt=""
                      id={`delete-faq-btn-${i + 1}`}
                    />
                  </li>
                </ul>
              ))}
            </TableWrapper>
            <div className="products-tabs-content-main">
              <form onSubmit={handleSubmitFaqs}>
                <div className="products-tabs-content">
                  <SimpleInput
                    value={faqState?.question}
                    onChange={(val) =>
                      setFaqState((prev) => ({ ...prev, question: val }))
                    }
                    isLabel={true}
                    labelText={"Question"}
                    isRequired={true}
                    type={"textArea"}
                    rows={3}
                    isLimit={300}
                    maxLength={300}
                    id="question-input"
                  />
                  <SimpleInput
                    value={faqState?.answer}
                    onChange={(val) =>
                      setFaqState((prev) => ({ ...prev, answer: val }))
                    }
                    isLabel={true}
                    labelText={"Answer"}
                    isRequired={true}
                    type={"textArea"}
                    rows={5}
                    isLimit={300}
                    maxLength={300}
                    id="answer-input"
                  />
                </div>
                <div className="flex justify-between my-6">
                  <PrimaryBtn
                    ButtonText="Add New Faq's"
                    btnExtraStyle={{
                      backgroundColor: "#00443f",
                      borderRadius: "1.5rem",
                    }}
                    type="submit"
                    disabled={payload?.faq?.length === 5}
                    title={
                      payload?.faq?.length === 5
                        ? "Maximum 5 FAQs allowed."
                        : ""
                    }
                    btnExtralClasses={
                      payload?.faq?.length === 5 ? "cursor-not-allowed" : ""
                    }
                    id="add-new-faq-btn"
                  />
                  <PrimaryBtn
                    ButtonText="Next"
                    btnExtraStyle={{
                      backgroundColor: "#00443f",
                      borderRadius: "1.5rem",
                    }}
                    onPress={() => handleTabClick("Image")}
                    type="button"
                    id="next-btn"
                  />
                </div>
              </form>
            </div>
          </div>
        ) : activeTab === "Image" ? (
          <div className="products-tabs-content-main">
            <div className="products-tabs-content">
              <div>
                <h3 className="font-size-18px text-[#2F2F31] mb-1 font-Poppins-Regular">
                  Image
                </h3>
                <ImageUpload
                  image={payload?.image}
                  onImageChange={(file: File | null) =>
                    setPayload((prev) => ({ ...prev, image: file }))
                  }
                  labelId="package-image-input"
                />
              </div>
            </div>
            <div className="flex justify-end my-6">
              <PrimaryBtn
                ButtonText="Back"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                }}
                onPress={() => handleTabClick("FAQ's")}
                id="back-btn"
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UpsertPackage;
